/**
 * Pure formatting for the failure-mode register page — DR-2026-352 v0.5.0 (D10–D12).
 *
 * No React, no JSX, deliberately: volto-polyglot has no test runner, and these are the
 * rules the page must never get wrong — above all D10's "a control never checked is never
 * rendered clean". `node --test tests/volto-polyglot/failure_mode_format.test.mjs` runs them.
 */

export const SEVERITY_COLOR = {
  critical: 'red', high: 'orange', medium: 'yellow', low: 'grey', info: 'grey',
};
export const MODE_STATUS_COLOR = {
  proposed: 'blue', active: 'green', merged: 'grey', retired: 'grey',
};
export const CAUGHT_ORDER = ['operator', 'self', 'detector', 'hook', 'audit', 'external', 'not_caught'];

/**
 * The register's day is Europe/Stockholm, not the viewer's and not UTC. Date-only
 * occurrences were written as Stockholm-midnight instants (DR-2026-352 v0.4.0 Task 9,
 * DST-aware), and the API serialises every timestamptz as UTC — so 31 August arrives as
 * "2026-08-30T22:00:00.000Z" (read live at Task 3, s624). Slicing the UTC string would
 * show the day before; formatting in the register's zone recovers the day in any browser
 * and under node --test regardless of the runner's TZ. sv-SE yields YYYY-MM-DD.
 */
const REGISTER_DAY = new Intl.DateTimeFormat('sv-SE', {
  timeZone: 'Europe/Stockholm', year: 'numeric', month: '2-digit', day: '2-digit',
});

/** YYYY-MM-DD in the register's zone from a timestamp string or Date; a dash for nothing. */
export function fmtDate(v) {
  if (!v) return '—';
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return REGISTER_DAY.format(d);
}

/** "operator 8 · self 12 · detector 3" in CAUGHT_ORDER; a dash when there is nothing. */
export function caughtBySummary(caught, labels = {}) {
  if (!caught) return '—';
  const parts = CAUGHT_ORDER.filter((k) => caught[k]).map((k) => `${labels[k] || k} ${caught[k]}`);
  return parts.length ? parts.join(' · ') : '—';
}

/**
 * D10 — the controls cell of a mode row. Three states and a never-clean NULL:
 *   no live control          → 'no control'          grey
 *   any missing              → 'N · M missing'       red
 *   none checked yet         → 'N · never checked'   grey
 *   some checked, some not   → 'N · K unchecked'     grey
 *   all checked, all exist   → 'N · exist (date)'    green
 */
export function controlsSummary(m) {
  const live = Number(m.n_controls_live) || 0;
  const missing = Number(m.n_missing) || 0;
  const unchecked = Number(m.n_unchecked) || 0;
  if (!live) return { text: 'no control', color: 'grey' };
  if (missing) return { text: `${live} · ${missing} missing`, color: 'red' };
  if (unchecked === live) return { text: `${live} · never checked`, color: 'grey' };
  if (unchecked) return { text: `${live} · ${unchecked} unchecked`, color: 'grey' };
  return { text: `${live} · exist (${fmtDate(m.last_checked_at)})`, color: 'green' };
}

/** D10 — one control's existence state, for the detail table. */
export function controlExistence(c) {
  if (c.artifact_checked_at == null) return { text: 'never checked', color: 'grey' };
  if (c.artifact_exists === false) {
    return { text: `missing (checked ${fmtDate(c.artifact_checked_at)})`, color: 'red' };
  }
  return { text: `exists (checked ${fmtDate(c.artifact_checked_at)})`, color: 'green' };
}

/** D11 — the queue's 5a row for a mode, as a chip; null when the queue holds none. */
export function recurredChip(r) {
  if (!r) return null;
  if (r.disposition) return { text: `5a ${r.disposition}`, color: 'grey' };
  if (r.actionability === 'decision') return { text: '5a decision pending', color: 'red' };
  return { text: '5a gate to build', color: 'orange' };
}

/** D11 — the queue's proposal row (failure_mode_new), as a chip; null when none. */
export function proposalChip(p) {
  if (!p) return null;
  if (p.disposition) return { text: `proposal ${p.disposition}`, color: 'grey' };
  return { text: 'proposal pending', color: 'blue' };
}
